import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  adminCitiesCache,
  adminTagsCache,
  adminUsersCache,
  adminUserSettingsCache,
  staticPreferencesCache,
} from '../../../common/constants/queryCacheKey';
import {
  AdminUserSettings,
  fetchStaticPreferences,
  fetchUserSettings,
  updateUserPhoto,
  updateUserSettings,
  UpdateUserSettingsInput,
  roleLabels,
} from '../../../common/services/users';
import {
  getSasUrl,
  PROFILE_PHOTOS_CONTAINER,
  uploadToBlob,
} from '../../../common/services/azureBlobUploader';
import {fetchAdminTags} from '../../../common/services/tags';
import {fetchAdminCities} from '../../../common/services/cities';
import {
  chipStyle,
  errorStyle,
  inputStyle,
  labelStyle,
  noticeStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../../admin/adminStyles';

interface SettingsForm {
  isConfigured: boolean;
  birthDate: string;
  locationCode: number;
  travelDistanceCodes: number[];
  activityTimeCodes: number[];
  vibeCodes: number[];
  categoryCodes: number[];
  tagCodes: number[];
}

const toForm = (s: AdminUserSettings): SettingsForm => ({
  isConfigured: s.isConfigured,
  birthDate: s.birthDate ?? '',
  locationCode: s.locationCode ?? 0,
  travelDistanceCodes: s.travelDistanceCodes,
  activityTimeCodes: s.activityTimeCodes,
  vibeCodes: s.vibeCodes,
  categoryCodes: s.categoryCodes,
  tagCodes: s.tagCodes,
});

const sameCodes = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

// Only the fields that changed are sent: a sent taste field recomputes the
// user's recommendation vector.
const changes = (
  saved: SettingsForm,
  form: SettingsForm,
): UpdateUserSettingsInput => {
  const input: UpdateUserSettingsInput = {};
  if (form.isConfigured !== saved.isConfigured) {
    input.isConfigured = form.isConfigured;
  }
  if (form.birthDate !== saved.birthDate && form.birthDate) {
    input.birthDate = form.birthDate;
  }
  if (form.locationCode !== saved.locationCode && form.locationCode) {
    input.locationCode = form.locationCode;
  }
  if (!sameCodes(form.travelDistanceCodes, saved.travelDistanceCodes)) {
    input.travelDistanceCodes = form.travelDistanceCodes;
  }
  if (!sameCodes(form.activityTimeCodes, saved.activityTimeCodes)) {
    input.activityTimeCodes = form.activityTimeCodes;
  }
  if (!sameCodes(form.vibeCodes, saved.vibeCodes)) {
    input.vibeCodes = form.vibeCodes;
  }
  if (!sameCodes(form.categoryCodes, saved.categoryCodes)) {
    input.categoryCodes = form.categoryCodes;
  }
  if (!sameCodes(form.tagCodes, saved.tagCodes)) {
    input.tagCodes = form.tagCodes;
  }
  return input;
};

const toggle = (codes: number[], code: number) =>
  codes.includes(code) ? codes.filter(c => c !== code) : [...codes, code];

const Section: React.FC<{
  title: string;
  hint?: string;
  children: React.ReactNode;
}> = ({title, hint, children}) => (
  <section style={{marginBottom: '22px'}}>
    <h3 style={sectionTitleStyle}>{title}</h3>
    {hint && <p style={hintStyle}>{hint}</p>}
    {children}
  </section>
);

export const UserSettingsPanel: React.FC<{
  userId: string;
  onClose: () => void;
}> = ({userId, onClose}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [tagFilter, setTagFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const settingsQuery = useQuery({
    queryKey: [adminUserSettingsCache, userId],
    queryFn: () => fetchUserSettings(userId),
  });
  const optionsQuery = useQuery({
    queryKey: [staticPreferencesCache],
    queryFn: fetchStaticPreferences,
    staleTime: Infinity,
  });
  const tagsQuery = useQuery({
    queryKey: [adminTagsCache],
    queryFn: fetchAdminTags,
  });
  const citiesQuery = useQuery({
    queryKey: [adminCitiesCache],
    queryFn: fetchAdminCities,
  });

  const settings = settingsQuery.data;
  const saved = useMemo(() => (settings ? toForm(settings) : null), [settings]);

  useEffect(() => {
    if (saved) setForm(saved);
  }, [saved]);

  // Closing asks first when there are unsaved changes; a ref so the Escape
  // listener sees the current form.
  const hasChangesRef = useRef(false);
  const requestClose = useRef(() => {});
  requestClose.current = () => {
    if (
      hasChangesRef.current &&
      !window.confirm('Close without saving your changes?')
    ) {
      return;
    }
    onClose();
  };

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) =>
      e.key === 'Escape' && requestClose.current();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Uploaded like the apps do (profile photos container, served from the
  // CDN origin) and saved at once; a new name each time so no cache keeps
  // showing the old photo.
  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Pick an image file.');
      return;
    }

    setIsUploadingPhoto(true);
    setError(null);
    setNotice(null);
    try {
      const sasUrl = await getSasUrl(PROFILE_PHOTOS_CONTAINER);
      if (!sasUrl) throw new Error('Could not get an upload link.');
      const extension = file.type === 'image/jpeg' ? 'jpg' : file.type.slice(6);
      const fileName = `avatar-${userId}-${Date.now()}.${extension}`;
      await uploadToBlob({sasUrl, file, fileName, quality: 0.85});
      const photoUrl = `${sasUrl.originBaseUrl}/${fileName}`;
      await updateUserPhoto(userId, photoUrl);

      queryClient.setQueryData<AdminUserSettings>(
        [adminUserSettingsCache, userId],
        current => (current ? {...current, profilePhoto: photoUrl} : current),
      );
      queryClient.invalidateQueries({queryKey: [adminUsersCache]});
      setNotice('Photo updated.');
    } catch (uploadError) {
      setError(
        `The photo could not be updated: ${
          uploadError instanceof Error ? uploadError.message : uploadError
        }`,
      );
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: (input: UpdateUserSettingsInput) =>
      updateUserSettings(userId, input),
    onSuccess: (updated, input) => {
      queryClient.setQueryData([adminUserSettingsCache, userId], updated);
      queryClient.invalidateQueries({queryKey: [adminUsersCache]});
      setError(null);
      const tasteChanged =
        input.activityTimeCodes !== undefined ||
        input.vibeCodes !== undefined ||
        input.categoryCodes !== undefined ||
        input.tagCodes !== undefined;
      setNotice(
        [
          'Saved.',
          tasteChanged
            ? "The user's recommendation vector was recomputed."
            : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
    },
    onError: (e: Error) => {
      setNotice(null);
      setError(e.message);
    },
  });

  const tagGroups = useMemo(() => {
    const data = tagsQuery.data;
    if (!data) return [];
    const keyword = tagFilter.trim().toLowerCase();
    return data.categories
      .map(category => ({
        category,
        tags: data.tags
          .filter(t => t.categoryCode === category.code && t.isForGoogleSearch)
          .filter(t => !keyword || t.name.toLowerCase().includes(keyword))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter(group => group.tags.length > 0);
  }, [tagsQuery.data, tagFilter]);

  const tagNames = useMemo(
    () => new Map((tagsQuery.data?.tags ?? []).map(t => [t.code, t.name])),
    [tagsQuery.data],
  );

  // Nothing to compare until the settings have loaded into the form.
  const pending: UpdateUserSettingsInput =
    saved && form ? changes(saved, form) : {};
  const hasChanges = Object.keys(pending).length > 0;
  hasChangesRef.current = hasChanges;
  const options = optionsQuery.data;

  const handleSave = () => {
    if (!hasChanges) return;
    setNotice(null);
    saveMutation.mutate(pending);
  };

  const update = (patch: Partial<SettingsForm>) =>
    setForm(current => (current ? {...current, ...patch} : current));

  return (
    <div style={overlayStyle} onClick={() => requestClose.current()}>
      <div style={drawerStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <label
            style={{
              ...photoButtonStyle,
              cursor: settings && !isUploadingPhoto ? 'pointer' : 'default',
            }}
            title="Change the photo">
            {settings?.profilePhoto ? (
              <img src={settings.profilePhoto} alt="" style={avatarStyle} />
            ) : (
              <div style={avatarStyle} />
            )}
            <span style={photoHintStyle}>
              {isUploadingPhoto ? 'Uploading...' : 'Change'}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhoto}
              disabled={!settings || isUploadingPhoto}
              style={{display: 'none'}}
            />
          </label>
          <div style={{flex: 1, minWidth: 0}}>
            <div
              style={{color: '#fff', fontWeight: 'bold', fontSize: '1.1rem'}}>
              {settings?.fullName ?? 'Loading...'}
            </div>
            <div style={{color: '#999', fontSize: '0.85rem'}}>
              {settings?.email}
            </div>
          </div>
          <button
            onClick={() => requestClose.current()}
            style={secondaryButtonStyle}>
            Close
          </button>
        </div>

        <div style={bodyStyle}>
          {settingsQuery.isError && (
            <p style={errorStyle}>Could not load this user's settings.</p>
          )}

          {form && settings && (
            <>
              <Section title="Account">
                <div style={fieldGridStyle}>
                  <label style={labelStyle}>
                    Role
                    <input
                      value={roleLabels[settings.roleCode ?? 0] ?? 'No role'}
                      readOnly
                      disabled
                      title="The role can't be changed here"
                      style={inputStyle}
                    />
                  </label>
                  <label style={labelStyle}>
                    Home city
                    <select
                      value={form.locationCode}
                      onChange={e =>
                        update({locationCode: Number(e.target.value)})
                      }
                      style={inputStyle}>
                      {!form.locationCode && <option value={0}>Not set</option>}
                      {(citiesQuery.data ?? []).map(city => (
                        <option key={city.code} value={city.code}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={labelStyle}>
                    Birth date
                    <input
                      type="date"
                      value={form.birthDate}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={e => update({birthDate: e.target.value})}
                      style={inputStyle}
                    />
                  </label>
                  <label style={{...labelStyle, justifyContent: 'flex-end'}}>
                    <span style={checkboxRowStyle}>
                      <input
                        type="checkbox"
                        checked={form.isConfigured}
                        onChange={e => update({isConfigured: e.target.checked})}
                      />
                      Onboarding finished
                    </span>
                  </label>
                </div>
                <p style={{...hintStyle, marginTop: '10px'}}>
                  Friend code: {settings.friendCode ?? '—'} · Push
                  notifications: {settings.hasDeviceToken ? 'on' : 'off'}
                  {settings.organizerName
                    ? ` · Business: ${settings.organizerName}`
                    : ''}
                </p>
              </Section>

              <Section
                title="Preferences"
                hint={
                  settings.hasPreferences
                    ? 'Changing the activity time, vibes, categories or tags recomputes the recommendation vector, keeping what was learned from the activity.'
                    : 'This user never finished onboarding: saving preferences creates them.'
                }>
                <div style={labelStyle}>
                  Travel distance
                  <div style={chipRowStyle}>
                    {(options?.travelDistances ?? []).map(o => (
                      <button
                        key={o.code}
                        type="button"
                        onClick={() =>
                          update({
                            travelDistanceCodes: toggle(
                              form.travelDistanceCodes,
                              o.code,
                            ),
                          })
                        }
                        style={chipStyle(
                          form.travelDistanceCodes.includes(o.code),
                        )}>
                        {o.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{...labelStyle, marginTop: '12px'}}>
                  Activity time
                  <div style={chipRowStyle}>
                    {(options?.activityTimes ?? []).map(o => (
                      <button
                        key={o.code}
                        type="button"
                        onClick={() =>
                          update({
                            activityTimeCodes: toggle(
                              form.activityTimeCodes,
                              o.code,
                            ),
                          })
                        }
                        style={chipStyle(
                          form.activityTimeCodes.includes(o.code),
                        )}>
                        {o.name}
                      </button>
                    ))}
                  </div>
                </div>
              </Section>

              <Section title="Vibes">
                <div style={chipRowStyle}>
                  {(options?.vibes ?? []).map(v => (
                    <button
                      key={v.code}
                      type="button"
                      onClick={() =>
                        update({vibeCodes: toggle(form.vibeCodes, v.code)})
                      }
                      style={chipStyle(form.vibeCodes.includes(v.code))}>
                      {v.name}
                    </button>
                  ))}
                </div>
              </Section>

              <Section title="Categories">
                <div style={chipRowStyle}>
                  {(tagsQuery.data?.categories ?? []).map(c => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() =>
                        update({
                          categoryCodes: toggle(form.categoryCodes, c.code),
                        })
                      }
                      style={chipStyle(form.categoryCodes.includes(c.code))}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </Section>

              <Section title={`Tags (${form.tagCodes.length})`}>
                {form.tagCodes.length > 0 && (
                  <div style={{...chipRowStyle, marginBottom: '12px'}}>
                    {form.tagCodes.map(code => (
                      <button
                        key={code}
                        type="button"
                        title="Remove"
                        onClick={() =>
                          update({tagCodes: toggle(form.tagCodes, code)})
                        }
                        style={chipStyle(true)}>
                        {tagNames.get(code) ?? `#${code}`} ×
                      </button>
                    ))}
                  </div>
                )}
                <input
                  placeholder="Find a tag to add..."
                  value={tagFilter}
                  onChange={e => setTagFilter(e.target.value)}
                  style={{...inputStyle, marginBottom: '12px'}}
                />
                <div style={tagListStyle}>
                  {tagGroups.map(({category, tags}) => (
                    <div key={category.code} style={{marginBottom: '12px'}}>
                      <div style={tagGroupTitleStyle}>{category.name}</div>
                      <div style={chipRowStyle}>
                        {tags.map(t => (
                          <button
                            key={t.code}
                            type="button"
                            onClick={() =>
                              update({tagCodes: toggle(form.tagCodes, t.code)})
                            }
                            style={chipStyle(form.tagCodes.includes(t.code))}>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            </>
          )}
        </div>

        <div style={footerStyle}>
          {error && <p style={{...errorStyle, margin: 0, flex: 1}}>{error}</p>}
          {notice && !error && (
            <p style={{...noticeStyle, margin: 0, flex: 1}}>{notice}</p>
          )}
          {!error && !notice && <span style={{flex: 1}} />}
          <button
            type="button"
            onClick={() => saved && setForm(saved)}
            disabled={!hasChanges}
            style={{...secondaryButtonStyle, opacity: hasChanges ? 1 : 0.5}}>
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            style={{...primaryButtonStyle, opacity: hasChanges ? 1 : 0.5}}>
            {saveMutation.isPending ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1200,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  justifyContent: 'flex-end',
};

const drawerStyle: React.CSSProperties = {
  width: 'min(640px, 100%)',
  height: '100%',
  backgroundColor: '#1f1f1f',
  borderLeft: '1px solid #333',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '20px',
  borderBottom: '1px solid #333',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '20px',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  borderTop: '1px solid #333',
};

const photoButtonStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
};

const photoHintStyle: React.CSSProperties = {
  color: '#daff00',
  fontSize: '0.75rem',
};

const avatarStyle: React.CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  objectFit: 'cover',
  backgroundColor: '#333',
  flexShrink: 0,
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#fff',
  fontSize: '0.95rem',
  margin: '0 0 10px',
};

const hintStyle: React.CSSProperties = {
  color: '#888',
  fontSize: '0.8rem',
  margin: '0 0 10px',
};

const fieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '14px',
};

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: '#ddd',
  fontSize: '0.85rem',
  height: '40px',
};

const chipRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const tagListStyle: React.CSSProperties = {
  maxHeight: '320px',
  overflowY: 'auto',
  paddingRight: '4px',
};

const tagGroupTitleStyle: React.CSSProperties = {
  color: '#999',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: '0 0 8px',
};
